import React, { useEffect, useState } from "react";
import { Camera, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import PrimaryButton from "../components/ui/PrimaryButton";
import { formatPercentage } from "../utils/format";
import { AnalysisResult, fetchHistory, ScanHistoryItem } from "../services/api";
import { useAuth } from "../context/AuthContext";
import "./ResultsPage.css";

type ResultsPageProps = {
  sampleId: string;
  photoUri: string | null;
  analysisResult: AnalysisResult | null;
};

type RiskBucket = "clear" | "moderate" | "risk";

const getRiskBucket = (riskScore: string): RiskBucket => {
  const score = parseFloat(riskScore);
  if (Number.isNaN(score)) return "clear";
  if (score >= 60) return "risk";
  if (score >= 30) return "moderate";
  return "clear";
};

const getBucketLabel = (bucket: RiskBucket, fallback?: string) => {
  if (fallback) return fallback;
  switch (bucket) {
    case "risk":
      return "High risk";
    case "moderate":
      return "Moderate";
    default:
      return "Low";
  }
};

const ResultsPage: React.FC<ResultsPageProps> = ({ photoUri, analysisResult }) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState<ScanHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { token } = useAuth();

  // Fetch history when page loads
  useEffect(() => {
    const loadHistory = async () => {
      setIsLoading(true);
      const data = await fetchHistory(token || undefined);
      setHistory(data);
      setIsLoading(false);
    };
    loadHistory();
  }, [token]);

  return (
    <div className="results">
      {analysisResult && (
        <div className="results__summary results__summary--clear">
          <div>
            <h2>Analysis Complete</h2>
            <p>Based on the AI assessment of your uploaded image.</p>
          </div>
        </div>
      )}

      <div className="results__grid">
        {analysisResult && (
          <Card padding="md" className="results__card">
            <h3>Current Analysis</h3>

            <div className="results__score-row">
              <span className="results__score-label">Risk Probability</span>
              <span className="results__score-value">
                {formatPercentage(analysisResult.melanoma_risk_score)}
              </span>
            </div>
            
            <div className="results__score-row">
              <span className="results__score-label">Risk Level</span>
              <span className="results__score-value">
                {analysisResult.risk_level}
              </span>
            </div>

            {photoUri && (
              <div className="results__image">
                <img src={photoUri} alt="Analyzed lesion" />
              </div>
            )}
          </Card>
        )}

          <Card padding="md" className="results__history-card">
          <h3>Previous results</h3>
          {isLoading ? (
            <p className="results__meta">Loading scan history...</p>
          ) : history.length > 0 ? (
            <div className="results__history-list">
              {history.map((scan, index) => (
                <div key={scan.timestamp + index} className="results__history-item">
                  <div className="results__history-info">
                    <p className="results__history-area">{`Scan #${scan.scanNumber}`}</p>
                    <span className="results__history-meta">
                      Captured: {new Date(parseInt(scan.timestamp) * 1000).toLocaleDateString()} {new Date(parseInt(scan.timestamp) * 1000).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="results__history-right">
                      {(() => {
                        const bucket = getRiskBucket(scan.riskScore);
                        const riskLabel = getBucketLabel(bucket, scan.riskLevel);
                        return (
                          <>
                            <div className="results__history-score-group">
                              <span
                                className={`results__history-score results__history-score--${bucket}`}
                              >
                                {parseFloat(scan.riskScore).toFixed(1)}%
                              </span>
                              <span
                                className={`results__history-severity results__history-severity--${bucket}`}
                              >
                                {riskLabel}
                              </span>
                            </div>
                            <ChevronRight size={16} strokeWidth={2.2} />
                          </>
                        );
                      })()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="results__meta">No previous scans found. Complete a scan to see results here.</p>
          )}
        </Card>
      </div>

      <div className="results__actions">
        <PrimaryButton
          variant="primary"
          icon={<Camera size={18} strokeWidth={2.2} />}
          onClick={() => {
            navigate("/scan");
          }}
        >
          Start another scan
        </PrimaryButton>
      </div>
    </div>
  );
};

export default ResultsPage;
